import { BillTo, MatchedLocation, ServiceLocation } from './typings'
import { LocationDTO } from '@sales-center/common'
import { getFormattedAddress } from '@sales-center/common'
import { IEmailCollectionItem } from '@sales-center/common'

export function isBillTo(
  location: BillTo | ServiceLocation,
): location is BillTo {
  return (location as ServiceLocation).billToId === undefined
}

export function locationToServiceLocationDTOAddOpp(
  location: ServiceLocation,
): LocationDTO {
  const {
    id: externalLinkId,
    number: locationCode,
    branchId,
    phone: phoneNumber,
    altPhone: altPhoneNumber,
    mobile: mobilePhone,
    email,
    locationTypeId,
    divisionId,
    address: {
      address: street1,
      address2: street2,
      city,
      state: stateAbbreviation,
      postalCode,
      county,
      country,
      latitude,
      longitude,
    },
  } = location

  return {
    ...location,
    branchId: Number(branchId),
    externalLinkId,
    externalLinkType: 'Location',
    locationCode,
    emailAddresses: email ? getEmailAddresses(email) : [],
    phoneNumber,
    altPhoneNumber,
    mobilePhone,
    street1,
    street2,
    city,
    stateAbbreviation,
    postalCode,
    county,
    country,
    latitude,
    longitude,
    id: +externalLinkId,
    contactId: -1,
    billingLocation: false,
    salesTeamId: -1,
    locationType: null,
    locationTypeId: locationTypeId,
    division: null,
    divisionId: divisionId,
    taxCode: (location as ServiceLocation)
      ? (location as ServiceLocation).taxCode
      : null,
    draftAgreementsUpdated: false,
  }
}

export function locationToBillToLocationDTOAddOpp(
  location: BillTo,
): LocationDTO {
  const {
    id: externalLinkId,
    number: locationCode,
    branchId,
    phone: phoneNumber,
    altPhone: altPhoneNumber,
    mobile: mobilePhone,
    email,
    billToTypeId,
    divisionId,
    address: {
      address: street1,
      address2: street2,
      city,
      state: stateAbbreviation,
      postalCode,
      county,
      country,
      latitude,
      longitude,
    },
  } = location

  return {
    ...location,
    branchId: Number(branchId),
    externalLinkId,
    externalLinkType: 'BillTo',
    locationCode,
    emailAddresses: email ? getEmailAddresses(email) : [],
    phoneNumber,
    altPhoneNumber,
    mobilePhone,
    street1,
    street2,
    city,
    stateAbbreviation,
    postalCode,
    county,
    country,
    latitude,
    longitude,
    id: +externalLinkId,
    contactId: -1,
    billingLocation: false,
    salesTeamId: -1,
    locationType: null,
    locationTypeId: !billToTypeId ? null : billToTypeId,
    division: null,
    divisionId: !divisionId ? null : divisionId,
    taxCode: null,
    draftAgreementsUpdated: false,
  }
}

export function locationToLocationDTO(
  location: BillTo | ServiceLocation,
): LocationDTO {
  const {
    id: externalLinkId,
    number: locationCode,
    branchId,
    phone: phoneNumber,
    altPhone: altPhoneNumber,
    mobile: mobilePhone,
    email,
    address: {
      address: street1,
      address2: street2,
      city,
      state: stateAbbreviation,
      postalCode,
      county,
      country,
      latitude,
      longitude,
    },
  } = location

  const billTo = isBillTo(location)

  return {
    ...location,
    branchId: Number(branchId),
    externalLinkId,
    externalLinkType: billTo ? 'BillTo' : 'Location',
    locationCode,
    emailAddresses: email ? getEmailAddresses(email) : [],
    phoneNumber,
    altPhoneNumber,
    mobilePhone,
    street1,
    street2,
    city,
    stateAbbreviation,
    postalCode,
    county,
    country,
    latitude,
    longitude,
    id: -1,
    contactId: -1,
    billingLocation: billTo,
    salesTeamId: -1,
    locationType: null,
    locationTypeId: !billTo
      ? (location as ServiceLocation).locationTypeId
      : null,
    division: null,
    divisionId: !billTo ? (location as ServiceLocation).divisionId : null,
    taxCode: (location as ServiceLocation)
      ? (location as ServiceLocation).taxCode
      : null,
    draftAgreementsUpdated: false,
  }
}

export function getEmailAddresses(email: string): IEmailCollectionItem[] {
  const emailsSplit = email.includes(',') ? email.split(',') : email.split(';')

  const usableEmails = emailsSplit.splice(0, 3)

  const splitEmails = usableEmails
    ? usableEmails.map((e, index) => ({
        emailAddress: e,
        isPrimary: index === 0,
      }))
    : [{ emailAddress: '', isPrimary: true }]
  return splitEmails
}

export function getEmailAddressesAsString(
  emailCollection: IEmailCollectionItem[] | undefined,
): string {
  if (!emailCollection || emailCollection.length == 0) return ''

  const primaryEmailAddress = emailCollection.find((x) => x.isPrimary)
  if (emailCollection.length == 1)
    return primaryEmailAddress
      ? primaryEmailAddress.emailAddress
      : emailCollection[0].emailAddress
  const secondaryEmails = emailCollection.filter((x) => !x.isPrimary)
  if (secondaryEmails && secondaryEmails.length > 0) {
    const result = [primaryEmailAddress, ...secondaryEmails]
      .map((x) => x?.emailAddress)
      .join(';')
    return result
  }
  return ''
}

export function getLocationSearchTerm(location: LocationDTO): string {
  const { lastName, company } = location

  const name = `${lastName ?? ''}`.trim()
  const companyName = (company ?? '').trim()
  const locationSearchTerm = name === '' ? companyName : name

  return locationSearchTerm
}

export function getLocationName(location: LocationDTO): string {
  const { firstName, lastName, company } = location

  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim()
  const companyName = (company ?? '').trim()
  const locationName = fullName === '' ? companyName : fullName

  return locationName
}

export function getLocationAddress(location: LocationDTO): string {
  const { street1, street2, city, stateAbbreviation, postalCode } = location

  return getFormattedAddress(
    street1 ?? '',
    street2 ?? '',
    city ?? '',
    stateAbbreviation ?? '',
    postalCode ?? '',
  )
}

export function getExternalLocationAddress(location: BillTo): string {
  const { address, address2, city, state, postalCode } = location.address

  return getFormattedAddress(address, address2, city, state, postalCode)
}

export function isMatchedLocation(
  location: LocationDTO,
): location is MatchedLocation {
  const { externalLinkId, locationCode } = location

  return Boolean(externalLinkId) && Boolean(locationCode)
}
